import { Box, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { NumberInputType4 } from "../../atoms/Input/NumberInput";

const ConsultationFeeTableWithWatch = ({
  speciality,
  ratesMap,
  control,
  appType,
}) => {
  const { t } = useTranslation();

  const watchedRates = useWatch({
    control,
    name: "specialityRates",
    defaultValue: {},
  });

  const formatCurrency = (value) => {
    if (value === "-" || value === undefined || value === null || value === "") return "-";
    const number = parseFloat(value);
    return isNaN(number)
      ? "0.00"
      : new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
  };

  const sortedSpecialties = [...speciality]
    .filter((spec) => spec && (spec.description || spec.label))
    .sort((a, b) => (a.description || "").localeCompare(b.description || ""));

  return (
    <Paper
      variant="White0.5"
      sx={{
        p: 0,
        border: "none",
        borderRadius: "8px",
        width: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "table",
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #dbdbddff",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Table Header */}
        <Box sx={{ display: "table-row", bgcolor: "#f2f2f7" }}>
          <Box sx={headerCellStyle}>{t("glossary:add-doctor.specialty")}</Box>
          <Box sx={{ ...headerCellStyle, textAlign: "left", width: "180px" }}>
            {t("glossary:add-doctor.online-fee")}
          </Box>
          <Box sx={{ ...headerCellStyle, textAlign: "left", width: "180px" }}>
            {t("glossary:add-doctor.walkin-fee")}
          </Box>
        </Box>

        {/* Table Body */}
        {sortedSpecialties.map((spec, idx) => {
          const specId = spec.id || spec.typeId;
          const currentFormRates = watchedRates?.[specId] || {};

          const patientRate = {
            online:
              currentFormRates.patientOnlinePrice ||
              ratesMap[specId]?.patientOnlinePrice ||
              spec.onlinePatientRate ||
              spec.OnlinePatientRate ||
              "",
            physical:
              currentFormRates.patientPhysicalPrice ||
              ratesMap[specId]?.patientPhysicalPrice ||
              spec.onsitePatientRate ||
              spec.OnsitePatientRate ||
              "",
          };

          const doctorRate = {
            online:
              currentFormRates.onlinePrice ||
              ratesMap[specId]?.onlinePrice ||
              spec.onlineRate ||
              spec.OnlineRate ||
              "",
            physical:
              currentFormRates.physicalPrice ||
              ratesMap[specId]?.physicalPrice ||
              spec.onsiteRate ||
              spec.OnsiteRate ||
              "",
          };

          const isLastRow = idx === sortedSpecialties.length - 1;

          return (
            <Box key={specId} sx={{ display: "table-row", bgcolor: "#FFFFFF" }}>
              {/* Specialty Name */}
              <Box sx={{ ...bodyCellStyle, borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                {spec.description}
              </Box>

              {/* Online Fee Cell */}
              <Box sx={{ ...bodyCellStyle, textAlign: "left", borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                <Box sx={{ display: appType === "doctor" ? "block" : "none" }}>
                  <NumberInputType4
                    name={`specialityRates.${specId}.onlinePrice`}
                    control={control}
                    label=""
                    placeholder={formatCurrency(doctorRate.online)}
                  />
                </Box>
                <Box sx={{ display: appType === "patient" ? "block" : "none" }}>
                  <NumberInputType4
                    name={`specialityRates.${specId}.patientOnlinePrice`}
                    control={control}
                    label=""
                    placeholder={formatCurrency(patientRate.online)}
                  />
                </Box>
              </Box>

              {/* Physical Fee Cell */}
              <Box sx={{ ...bodyCellStyle, textAlign: "left", borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                <Box sx={{ display: appType === "doctor" ? "block" : "none" }}>
                  <NumberInputType4
                    name={`specialityRates.${specId}.physicalPrice`}
                    control={control}
                    label=""
                    placeholder={formatCurrency(doctorRate.physical)}
                  />
                </Box>
                <Box sx={{ display: appType === "patient" ? "block" : "none" }}>
                  <NumberInputType4
                    name={`specialityRates.${specId}.patientPhysicalPrice`}
                    control={control}
                    label=""
                    placeholder={formatCurrency(patientRate.physical)}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

// Read-only table (no form control required)
const ConsultationFeeTableReadOnly = ({ speciality, ratesMap, appType }) => {
  const { t } = useTranslation();

  const formatCurrency = (value) => {
    if (value === "-" || value === undefined || value === null || value === "") return "-";
    const number = parseFloat(value);
    return isNaN(number)
      ? "0.00"
      : new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(number);
  };

  const sortedSpecialties = [...speciality]
    .filter((spec) => spec && (spec.description || spec.label))
    .sort((a, b) => (a.description || "").localeCompare(b.description || ""));

  return (
    <Paper
      variant="White0.5"
      sx={{
        p: 3,
        border: "1px solid #dbdbddff",
        borderRadius: "8px",
        width: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "table",
          width: "100%",
          borderCollapse: "collapse",
          border: "1px solid #dbdbddff",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "table-row", bgcolor: "#f2f2f7" }}>
          <Box sx={headerCellStyle}>{t("glossary:add-doctor.specialty")}</Box>
          <Box sx={{ ...headerCellStyle, textAlign: "right" }}>
            {t("glossary:add-doctor.online-fee")}
          </Box>
          <Box sx={{ ...headerCellStyle, textAlign: "right" }}>
            {t("glossary:add-doctor.walkin-fee")}
          </Box>
        </Box>

        {sortedSpecialties.map((spec, idx) => {
          const specId = spec.id || spec.typeId;

          const patientRate = {
            online:
              ratesMap[specId]?.patientOnlinePrice ||
              spec.onlinePatientRate ||
              spec.OnlinePatientRate ||
              "",
            physical:
              ratesMap[specId]?.patientPhysicalPrice ||
              spec.onsitePatientRate ||
              spec.OnsitePatientRate ||
              "",
          };

          const doctorRate = {
            online:
              ratesMap[specId]?.onlinePrice ||
              spec.onlineRate ||
              spec.OnlineRate ||
              "",
            physical:
              ratesMap[specId]?.physicalPrice ||
              spec.onsiteRate ||
              spec.OnsiteRate ||
              "",
          };

          const activeRate = appType === "patient" ? patientRate : doctorRate;
          const isLastRow = idx === sortedSpecialties.length - 1;

          return (
            <Box key={specId} sx={{ display: "table-row", bgcolor: "#FFFFFF" }}>
              <Box sx={{ ...bodyCellStyle, borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                {spec.description}
              </Box>
              <Box sx={{ ...bodyCellStyle, textAlign: "right", borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                <Typography sx={readOnlyTextStyle}>{formatCurrency(activeRate.online)}</Typography>
              </Box>
              <Box sx={{ ...bodyCellStyle, textAlign: "right", borderBottom: isLastRow ? "none" : "1px solid #dbdbddff" }}>
                <Typography sx={readOnlyTextStyle}>{formatCurrency(activeRate.physical)}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

const ConsultationFeeTable = ({
  speciality = [],
  ratesMap = {},
  isEdit = false,
  control,
  appType = "doctor",
}) => {
  if (!speciality || speciality.length === 0) return null;

  if (isEdit && control) {
    return (
      <ConsultationFeeTableWithWatch
        speciality={speciality}
        ratesMap={ratesMap}
        control={control}
        appType={appType}
      />
    );
  }

  return (
    <ConsultationFeeTableReadOnly
      speciality={speciality}
      ratesMap={ratesMap}
      appType={appType}
    />
  );
};

const headerCellStyle = {
  display: "table-cell",
  p: 1.5,
  fontWeight: 700,
  fontSize: "13px",
  color: "#494949ff",
  borderBottom: "1px solid #dbdbddff",
};

const bodyCellStyle = {
  display: "table-cell",
  p: 1.5,
  fontSize: "13px",
  fontWeight: 500,
  verticalAlign: "top",
  color: "#4e4e4eff",
};

const readOnlyTextStyle = {
  fontSize: "13px",
  color: "#6c6c6cff",
  fontWeight: 600,
};

export default ConsultationFeeTable;